import { Store } from '~/lib/data-contract/salesforce';
import type { Coordinates } from './coordinates';
import { haversineDistance } from './haversineDistance';

/**
 * Finds and returns a list of stores that are within a specified maximum Haversine distance
 * from a given origin point, sorted by proximity.
 * @function
 *
 * @param {Coordinates} origin - The origin point containing latitude and longitude coordinates.
 *                          This is used as the reference point for calculating distances.
 * @param {Store[]} data - An array of store objects to search through. Each store object must contain `latitude` and `longitude` properties.

 * @param {number} [maxHaversineDistance=40] - The maximum Haversine distance (in kilometers) within which stores should be returned. Defaults to 40 km.
 *
 * @returns {Store[]} An array of store objects that are within the specified maximum Haversine distance,
 *                    sorted by their distance from the origin in ascending order.
 */

export function findClosestStores(
    origin: Coordinates,
    data: Store[] = [],
    maxHaversineDistance = 40,
): Store[] {
    return data
        .map((store) => {
            if (typeof store.latitude === 'number' && typeof store.longitude === 'number') {
                const haversineDist = haversineDistance(origin, {
                    latitude: store.latitude,
                    longitude: store.longitude,
                });
                return { store, haversineDist };
            }
            return null;
        })
        .filter(
            (entry): entry is { store: Store; haversineDist: number } =>
                entry !== null && entry.haversineDist <= maxHaversineDistance,
        )
        .sort((a, b) => a.haversineDist - b.haversineDist)
        .map((entry) => entry.store);
}
